import React, { useState } from 'react';
import ImageviewModal from './ImageviewModal';

/**
 * GeneratedImageCard - Displays AI-generated images with download and view options
 * 
 * Used when manage_image_generation tool returns image URLs
 */
const GeneratedImageCard = ({ images, prompt }) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  if (!images || images.length === 0) return null;

  const handleViewImage = (imageUrl) => {
    setSelectedImage(imageUrl);
    setShowModal(true);
  };

  const handleDownload = async (imageUrl, index) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `generated-image-${Date.now()}-${index}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      // Fallback: open in new tab
      window.open(imageUrl, '_blank');
    }
  };

  const handleOpenNewTab = (imageUrl) => {
    window.open(imageUrl, '_blank');
  };

  return (
    <div className="generated-image-card" style={{
      marginTop: '12px',
      marginBottom: '12px',
    }}>
      {/* Image Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: images.length === 1 ? '1fr' : 'repeat(2, 1fr)',
        gap: '12px',
        maxWidth: images.length === 1 ? '400px' : '500px',
      }}>
        {images.map((image, index) => {
          const imageUrl = typeof image === 'string' ? image : image.url;
          return (
            <div 
              key={index}
              style={{
                position: 'relative',
                borderRadius: '12px',
                overflow: 'hidden',
                backgroundColor: '#1a1a2e',
                border: '1px solid rgba(255,255,255,0.1)',
              }}
            >
              {/* Image */}
              <img
                src={imageUrl}
                alt={`Generated image ${index + 1}`}
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease',
                }}
                onClick={() => handleViewImage(imageUrl)}
                onMouseOver={(e) => e.target.style.transform = 'scale(1.02)'}
                onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
              />
              
              {/* Action Buttons Overlay */}
              <div style={{
                position: 'absolute',
                bottom: '8px',
                right: '8px',
                display: 'flex',
                gap: '6px',
              }}>
                {/* View Full Size */}
                <button
                  onClick={() => handleViewImage(imageUrl)}
                  title="View full size"
                  style={{
                    background: 'rgba(0,0,0,0.7)',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background 0.2s',
                  }}
                  onMouseOver={(e) => e.target.style.background = 'rgba(0,0,0,0.9)'}
                  onMouseOut={(e) => e.target.style.background = 'rgba(0,0,0,0.7)'}
                >
                  <i className="bi bi-arrows-fullscreen" style={{ color: 'white', fontSize: '14px' }}></i>
                </button>

                {/* Open in New Tab */}
                <button
                  onClick={() => handleOpenNewTab(imageUrl)}
                  title="Open in new tab"
                  style={{
                    background: 'rgba(0,0,0,0.7)',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background 0.2s',
                  }}
                  onMouseOver={(e) => e.target.style.background = 'rgba(0,0,0,0.9)'}
                  onMouseOut={(e) => e.target.style.background = 'rgba(0,0,0,0.7)'}
                >
                  <i className="bi bi-box-arrow-up-right" style={{ color: 'white', fontSize: '14px' }}></i>
                </button>

                {/* Download */}
                <button
                  onClick={() => handleDownload(imageUrl, index)}
                  title="Download image"
                  style={{
                    background: 'rgba(26, 115, 232, 0.9)',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background 0.2s',
                  }}
                  onMouseOver={(e) => e.target.style.background = 'rgba(26, 115, 232, 1)'}
                  onMouseOut={(e) => e.target.style.background = 'rgba(26, 115, 232, 0.9)'}
                >
                  <i className="bi bi-download" style={{ color: 'white', fontSize: '14px' }}></i>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Prompt Caption (optional) */}
      {prompt && (
        <p style={{
          marginTop: '8px',
          fontSize: '12px',
          color: 'rgba(255,255,255,0.5)',
          fontStyle: 'italic',
          maxWidth: '500px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          Prompt: {prompt}
        </p>
      )}

      {/* Full Screen Modal */}
      <ImageviewModal
        show={showModal}
        onHide={() => setShowModal(false)}
        fileUrl={selectedImage}
        fileType="image"
        altText="Generated Image"
        fileName="generated-image.png"
      />
    </div>
  );
};

export default GeneratedImageCard;
